import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, RotateCcw } from 'lucide-react';
import confetti from 'canvas-confetti';
import axios from 'axios';

const MASCOT_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/74fa619aec3bc4f2ce6f55b0bc0a86dee551f7ad9bd9a596e5a33b7f45e9610e.png';
const STAR_URL =
  'https://static.prod-images.emergentagent.com/jobs/0e7ec82b-be21-4167-ae09-299c58207254/images/51c2f4d391b08733a1282c5c803c88045bd7e3e5993310b66fbe2ae387825c7c.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedRef = useRef(false);

  const mode = searchParams.get('mode') || 'practice';
  const operation = searchParams.get('operation') || 'addition';
  const score = parseInt(searchParams.get('score') || '0', 10);
  const total = parseInt(searchParams.get('total') || '10', 10);
  const time = parseInt(searchParams.get('time') || '0', 10);

  const percentage = total > 0 ? Math.round((score / total) * 100) : 0;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;

  useEffect(() => {
    // Save game result
    if (!savedRef.current) {
      savedRef.current = true;
      axios
        .post(`${API}/games`, {
          player_name: localStorage.getItem('gaurik_player_name') || 'Player',
          mode,
          operation,
          score,
          total_questions: total,
          time_taken: time,
        })
        .catch(console.error);
    }

    // Celebration confetti
    if (percentage >= 50) {
      const duration = 2500;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#3b82f6'],
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#3b82f6'],
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [percentage, mode, operation, score, total, time]);

  const getMessage = () => {
    if (percentage >= 90) return 'AMAZING JOB!';
    if (percentage >= 70) return 'GREAT WORK!';
    if (percentage >= 50) return 'GOOD TRY!';
    return 'KEEP PRACTICING!';
  };

  const getSubMessage = () => {
    if (percentage >= 90) return "You're a math superstar!";
    if (percentage >= 70) return "You're doing great!";
    if (percentage >= 50) return 'Almost there, keep going!';
    return "Practice makes perfect!";
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <div className="glass-panel p-8 max-w-md w-full text-center">
        {/* Mascot */}
        <img
          src={MASCOT_URL}
          alt="mascot"
          className="w-28 h-28 mx-auto mb-4 animate-bounce-slow rounded-full bg-white/80 p-2"
          data-testid="results-mascot"
        />

        {/* Message */}
        <h1
          data-testid="results-message"
          className="font-heading text-3xl sm:text-4xl font-black text-amber-950 mb-1"
        >
          {getMessage()}
        </h1>
        <p className="font-body text-base text-amber-800 mb-5">{getSubMessage()}</p>

        {/* Stars */}
        <div className="flex justify-center gap-3 mb-5" data-testid="results-stars">
          {[1, 2, 3].map((i) => (
            <img
              key={i}
              src={STAR_URL}
              alt={`star ${i}`}
              className={`w-14 h-14 animate-star-in ${
                i <= stars ? '' : 'grayscale opacity-30'
              }`}
              style={{ animationDelay: `${i * 250}ms` }}
            />
          ))}
        </div>

        {/* Score */}
        <div className="mb-2">
          <p
            data-testid="results-score"
            className="font-heading text-5xl sm:text-6xl font-black text-amber-950"
          >
            {score}/{total}
          </p>
        </div>
        <p className="font-body text-lg font-bold text-amber-800 mb-6">{percentage}% correct</p>

        {/* Mode/Operation info */}
        <div className="flex justify-center gap-2 mb-6">
          <span className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded-full">
            {mode === 'test' ? 'Test Mode' : 'Practice'}
          </span>
          <span className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded-full capitalize">
            {operation}
          </span>
          {time > 0 && (
            <span className="bg-amber-100 text-amber-800 font-bold text-sm px-3 py-1 rounded-full">
              {Math.floor(time / 60)}m {time % 60}s
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <button
            data-testid="play-again"
            onClick={() => navigate(`/game?mode=${mode}&operation=${operation}`)}
            className="bg-orange-500 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-orange-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-orange-400 flex items-center justify-center gap-2 shadow-lg"
          >
            <RotateCcw className="w-6 h-6" />
            Play Again
          </button>
          <button
            data-testid="go-home"
            onClick={() => navigate('/jungle-math')}
            className="bg-emerald-500 text-white font-black text-lg py-4 px-8 rounded-full border-b-[6px] border-emerald-700 active:border-b-0 active:translate-y-[6px] transition-all hover:bg-emerald-400 flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-6 h-6" />
            Home
          </button>
        </div>
      </div>
    </div>
  );
}
